<template>
  <div v-if="getError('variant.base')" class="bg-red-50 p-2 rounded-lg text-red-500 mb-3">
    <span class="text-sm">{{ getError('variant.base') }}</span>
  </div>
  
  <form @submit.prevent="submitForm" @input="dirty = true">
    <div>
      <label for="text" class="text-sm font-medium text-gray-700 flex">
        {{ t('slides_editor.components.question_form.question') }}
      </label>

      <textarea
        v-model="question.text"
        @input="debouncedSubmit"
        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-black/50"
      />
      <div v-if="getError('text')" class="text-red-500 text-sm">{{ getError('text') }}</div>
    </div>

    <VariantQuizFields
      :question="question"
      :is-spark="true"
      @on-input="debouncedSubmit"/>

    <div v-if="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</div>
  </form>
</template>

<script setup>
import { ref, inject, watch, onBeforeUnmount } from 'vue'
import debounce from 'debounce'
import { VariantQuizFields } from '../../questions'
import { 
  useQuestionHelpers, 
  useBlockUpdate, 
  useSaveStatus, 
  useSidePanelHelpers 
} from '@/features/slides_editor/composables'
import { t } from '@/libs/i18n'

const props = defineProps({
  question: { type: Object, required: true },
  block: { type: Object, required: true },
  isNew: { type: Boolean, default: false }
})

const errorMessage = ref('')
const dirty = ref(false)
const initialState = { ...props.question }

const slides = inject('parsedSlides')
const errors = inject('errors')

const { onAfterCreate, syncSavedIds } = useQuestionHelpers()
const { onBlockUpdate } = useBlockUpdate(props.block, { showErrorStatus: false })
const { resetErrors } = useSaveStatus()
const { fixSidePanel, unfixSidePanel } = useSidePanelHelpers()

const getError = (field) => {
  const errorPath = `section.section_spark.spark.questions.${field}`
  return errors.value[errorPath]?.[0]
}

const submitForm = async () => {
  const { section } = props.block
  const { question } = props

  const sectionParams = {
    id: section.id,
    section_spark_attributes: {
      id: section.section_spark_id,
      spark_id: section.spark.id,
      spark_attributes: {
        id: section.spark.id,
        title: section.spark.title,
        questions_attributes: section.spark.questions
      }
    }
  }

  const response = await onBlockUpdate(sectionParams, 'section_attributes')
  if (!response) return

  if (question.id) {
    syncSavedIds(section.spark.questions, savedQuestions(response))
  } else {
    onAfterCreate(slides, response, {
      content: { component: 'SparkQuestionForm' },
      getQuestion: (block) => block.section.spark.questions[block.section.spark.questions.length - 1]
    })
  }
}

const savedQuestions = (response) => {
  return response.slide.blocks.find(b => b.block_type === 'section')?.section?.spark?.questions || []
}

const debouncedSubmit = debounce(submitForm, 1000)

watch(errors, (newErrors) => {
  if (Object.keys(newErrors).length > 0) {
    fixSidePanel()
  } else {
    unfixSidePanel()
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (Object.keys(errors.value).length > 0 && !dirty.value) {
    if (props.isNew) {
      props.block.section.spark.questions.pop()
    } else {
      Object.assign(props.question, initialState)
    }

    resetErrors()
  }
})
</script>

<style>
  textarea {
    field-sizing: content;
  }
</style>
